import React, { useCallback, useEffect, useState } from 'react'
import { DraftCommentProvider } from '@sketch/modules-common'
import { AnnotationOverlayContext } from '../../../../annotations/components'
import {
  useLockCamera,
  useGetNodeAtPosition,
  useMapRelativePositionToCameraPosition,
  useStatus,
} from '@sketch-hq/sketch-web-renderer'
import { MouseCoordinates } from '../../../../annotations/types'
import { AnnotationDotFragment } from '@sketch/gql-types'
import { useHideAnnotationDots } from '../../../../annotations/hooks'
import { getAnnotatableNode } from '../../../../annotations/utils'

interface PageAnnotationsOverlayContextProps
  extends Pick<
    React.ComponentProps<typeof AnnotationOverlayContext>,
    'disabled' | 'children' | 'hidden'
  > {
  isViewingLatestVersion: boolean
  permanentPageId: string
}

const PageAnnotationsOverlayContext = (
  props: PageAnnotationsOverlayContextProps
) => {
  const { permanentPageId, hidden, ...otherProps } = props

  const status = useStatus()
  const lockCamera = useLockCamera()
  const [lockCameraState, setLockCameraState] = useState(false)

  useEffect(() => {
    lockCamera(lockCameraState)
  }, [lockCamera, lockCameraState])

  const getNodeAtPosition = useGetNodeAtPosition()
  const mapRelativePosition = useMapRelativePositionToCameraPosition()

  const getElementAtPosition = useCallback(
    async (mouseCoordinates: MouseCoordinates) => {
      const node = await getNodeAtPosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )
      const annotatable = getAnnotatableNode(node)

      if (annotatable?.type === 'ARTBOARD') {
        return {
          type: 'ARTBOARD' as const,
          permanentId: annotatable.node.getLayerUUID(),
          bounds: annotatable.node.getAbsoluteBounds(),
          permanentPageId,
        }
      }

      if (annotatable?.type === 'SECTION') {
        return {
          type: 'SECTION' as const,
          permanentId: annotatable.node.getLayerUUID(),
          bounds: annotatable.node.getAbsoluteBounds(),
          permanentPageId,
        }
      }

      return {
        type: 'PAGE' as const,
        permanentId: permanentPageId,
      }
    },
    [getNodeAtPosition, permanentPageId]
  )

  const getPositionRelativeToOrigin = useCallback(
    (mouseCoordinates: MouseCoordinates) => {
      if (status.type !== 'READY') {
        return null
      }

      return mapRelativePosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )
    },
    [mapRelativePosition, status]
  )

  const shouldRenderAnnotation = useCallback(
    (annotationDot: AnnotationDotFragment) => {
      if (annotationDot.currentSubject.__typename === 'Artboard') {
        return annotationDot.currentSubject.page?.uuid === permanentPageId
      }
      if (annotationDot.currentSubject.__typename === 'Page') {
        return annotationDot.currentSubject.pageUUID === permanentPageId
      }

      return false
    },
    [permanentPageId]
  )

  const [hideAnnotationsDots] = useHideAnnotationDots()
  return (
    <DraftCommentProvider>
      <AnnotationOverlayContext
        onMovingAnnotation={setLockCameraState}
        getPositionRelativeToOrigin={getPositionRelativeToOrigin}
        getElementAtPosition={getElementAtPosition}
        hidden={hideAnnotationsDots || hidden}
        shouldRenderAnnotation={shouldRenderAnnotation}
        {...otherProps}
      />
    </DraftCommentProvider>
  )
}

export default PageAnnotationsOverlayContext
